import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { Dumbbell, Calendar, Clock, Users, ArrowRight } from "lucide-react";
import { useLocation } from "wouter";
import { GymHeader } from "@/components/GymHeader";

export default function GymLanding() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  const { data: config } = trpc.gym.getConfig.useQuery();
  const { data: services } = trpc.gym.services.list.useQuery({});

  const isGymMember = user?.roles?.includes("gym_member");
  const gymStatus = (user as any)?.gymStatus;

  const handleJoin = () => {
    if (!user) {
      // Redirect to login with gym registration flag
      window.location.href = getLoginUrl(undefined, { gymRegistration: true });
      return;
    }
    // User is logged in, go to registration
    setLocation("/gym/register");
  };

  if (!config?.enabled) {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12 text-center">
          <h1 className="text-2xl font-bold">Fitnessstudio</h1>
          <p className="text-muted-foreground mt-4">
            Das Fitnessstudio ist derzeit nicht verfügbar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GymHeader />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-background py-20">
        <div className="container">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Willkommen im <span className="text-primary">Fitnessstudio</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">
              Trainieren Sie flexibel mit unserem Slot-Buchungssystem. 
              Wählen Sie Ihren bevorzugten Trainingsbereich und buchen Sie 
              Ihren persönlichen Zeitslot.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {isGymMember && gymStatus === "approved" ? (
                <Button size="lg" onClick={() => setLocation("/gym/calendar")}>
                  <Calendar className="h-5 w-5 mr-2" />
                  Slot buchen
                </Button>
              ) : gymStatus === "pending" ? (
                <Button size="lg" disabled>
                  <Clock className="h-5 w-5 mr-2" />
                  Anfrage wird bearbeitet...
                </Button>
              ) : (
                <Button size="lg" onClick={handleJoin}>
                  <Dumbbell className="h-5 w-5 mr-2" />
                  Jetzt Mitglied werden
                </Button>
              )}
              <Button size="lg" variant="outline" onClick={() => setLocation("/gym/calendar")}>
                Verfügbarkeit ansehen
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container">
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader>
                <Calendar className="h-10 w-10 text-primary mb-2" />
                <CardTitle>Flexible Buchung</CardTitle>
                <CardDescription>
                  Buchen Sie Ihre Trainingsslots online - wann und wo Sie möchten
                </CardDescription>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <Users className="h-10 w-10 text-primary mb-2" />
                <CardTitle>Begrenzte Plätze</CardTitle>
                <CardDescription>
                  Maximale Kapazität pro Slot für optimales Training
                </CardDescription>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <Clock className="h-10 w-10 text-primary mb-2" />
                <CardTitle>Einfacher Check-in</CardTitle>
                <CardDescription>
                  Schneller Self-Service Check-in per QR-Code
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Services */}
      {services && services.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container">
            <h2 className="text-3xl font-bold text-center mb-12">
              Unsere Trainingsbereiche
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {services.filter(s => s.isActive).map(service => (
                <Card key={service.id} className="overflow-hidden">
                  <div 
                    className="h-2" 
                    style={{ backgroundColor: service.color }}
                  />
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: service.color }}
                      />
                      {service.name}
                    </CardTitle>
                    {service.description && (
                      <CardDescription>{service.description}</CardDescription>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Opening Hours */}
      {config?.gym?.openingHours && (
        <section className="py-16">
          <div className="container">
            <h2 className="text-3xl font-bold text-center mb-12">
              Öffnungszeiten
            </h2>
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  {Object.entries(config.gym.openingHours).map(([day, hours]) => {
                    const dayNames: Record<string, string> = {
                      monday: "Montag",
                      tuesday: "Dienstag",
                      wednesday: "Mittwoch",
                      thursday: "Donnerstag",
                      friday: "Freitag",
                      saturday: "Samstag",
                      sunday: "Sonntag"
                    };
                    const h = hours as { open: string; close: string } | null;
                    return (
                      <div key={day} className="flex justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{dayNames[day] || day}</span>
                        <span className="text-muted-foreground">
                          {h ? `${h.open} - ${h.close}` : "Geschlossen"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-primary/5">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">
            Bereit zum Trainieren?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Werden Sie Mitglied und buchen Sie Ihren ersten Trainingsslot noch heute.
          </p>
          {isGymMember && gymStatus === "approved" ? (
            <Button size="lg" onClick={() => setLocation("/gym/calendar")}>
              Jetzt Slot buchen
            </Button>
          ) : gymStatus === "pending" ? (
            <p className="text-muted-foreground">
              Ihre Mitgliedsanfrage wird bearbeitet. Wir melden uns bei Ihnen.
            </p>
          ) : (
            <Button size="lg" onClick={handleJoin}>
              Mitglied werden
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
